/* eslint-disable no-console */
import _get from 'lodash/get';
import { Google, Pixall } from '../analytics/index.js';
import { getSortTypeTextHelper, sanitizeValue } from './helpers/index.js';
import { filterClickLabelTransformer } from '../transformers/pixall/index.js';
import { filterNameTransformer, pageNameTransformer, filterClickEventDataTransformer } from '../transformers/google/index.js';

/**
 * @summary Sends Google click event for turning on compare toggle
 * @events srp event43
 */
export async function compareToggleClick(_ref, option) {
  let {
    data = {}
  } = _ref;
  Google.sendClickEvent({
    eventElement: 'compare',
    eventResult: 'toggle::open::compare'
  }, option);
  return {};
}

/**
 * @summary Sends an Adobe link track for changing number of records on SRP
 * @cmp numRecords
 * @events event1
 * @requires data.recordAmount
 */
export async function numRecordsDropDownClick(_ref2, option) {
  let {
    data = {},
    dataLayer
  } = _ref2;
  const {
    recordAmount = 25
  } = data;
  const pageName = _get(dataLayer, 'page.pageName', '');
  Google.sendClickEvent({
    eventElement: pageNameTransformer(pageName) || 'search-car',
    eventResult: `dropdown::show-${recordAmount}`
  }, option);
  return {
    cmp: 'numRecords'
  };
}

/**
 * @summary Sends an Adobe link track for paginating on SRP
 * @cmp pagination
 * @events event1
 * @requires data.pageNumber,
 */
export async function paginationClick(_ref3, option) {
  let {
    data = {},
    state,
    filterDuck,
    paginationDuck,
    resultsDuck,
    userDuck,
    dataLayer
  } = _ref3;
  if (!filterDuck) {
    console.error('filterDuck is missing');
  }
  if (!paginationDuck) {
    console.error('paginationDuck is missing');
  }
  if (!resultsDuck) {
    console.error('resultsDuck is missing');
  }
  if (!userDuck) {
    console.error('userDuck is missing');
  }
  const pagination = paginationDuck ? paginationDuck.selectors.getDuckState(state) : {};
  const searchResults = resultsDuck ? resultsDuck.selectors.getDuckState(state) : {};
  const pageName = _get(dataLayer, 'page.pageName', '');
  const extra = {
    cmp: 'pagination',
    co_txt_url: 'Page ' + data.pageNumber,
    pagination,
    searchResults
  };
  Google.sendClickEvent({
    eventElement: pageNameTransformer(pageName) || 'search-car',
    eventAction: 'paginate',
    // this will be the next page number they are clicking to 'eventValue': '',
    eventResult: `page::${data.pageNumber}`,
    eventData: {}
  }, option);
  Pixall.sendClickEvent({
    eventSource: 'field',
    eventType: 'change',
    label: `page::${data.pageNumber}`,
    hasListingType: false
  }, dataLayer, option);
  return extra;
}

/**
 * @summary Sends a Google/Pixall click event on search radius and zip value change
 * @cmp searchRadius
 * @events event 96
 */
export async function searchRadiusClick(_ref4, option) {
  let {
    data = {},
    event,
    dataLayer
  } = _ref4;
  const targetValue = event?.target?.value;
  const isZip = data.filterName === 'zip';
  // If new zip is entered: Pass entered zip value, pass the radius for radius, i.e. in GTM: radius::[entered zip]
  // If new search radius is selected: Pass selected radius, pass zip for zip, i.e. in GTM: [selected radius]::zip
  const eventResult = isZip ? `radius::${targetValue}` : `${targetValue}::zip`;
  const pageName = _get(dataLayer, 'page.pageName', '');
  Google.sendClickEvent({
    eventElement: pageNameTransformer(pageName) || 'search-car',
    eventAction: 'filter',
    eventResult,
    eventData: {
      search: {
        [isZip ? 'zipCode' : 'radius']: targetValue
      }
    }
  }, option);
  Pixall.sendClickEvent({
    eventSource: 'field',
    eventType: 'change',
    label: eventResult,
    hasListingType: false
  }, dataLayer, option);
  return {};
}

/**
 * @summary Sends an Adobe/Google link track for sorting on SRP
 * @cmp sortBy
 * @events event1
 * @requires data.sortType
 */
export async function sortDropDownClick(_ref5, option) {
  let {
    data = {},
    dataLayer
  } = _ref5;
  Pixall.sendClickEvent({
    eventSource: 'field',
    eventType: 'change',
    label: `sort::${getSortTypeTextHelper(data.sortType)}`,
    hasListingType: false,
    cdl: {
      eventProperties: {
        tagIndex: 'srp-074'
      }
    }
  }, dataLayer, option);
  return {};
}

/**
 * @summary Sends a Google click event on filter value change
 * @cmp filter
 * @events event1
 */

export async function filterClick(_ref6, option) {
  let {
    data = {},
    event,
    dataLayer,
    filterDuck,
    resultsDuck,
    state
  } = _ref6;
  const {
    filterName,
    filterTitle
  } = data;
  const pageName = _get(dataLayer, 'page.pageName', '');
  const {
    brand = ''
  } = state;
  // Allow for deprecated usage of the direct state lookup in favor of resultsDuck selector
  const deprecatedSelector = _state => _get(_state, `domain.${pageName}.results`, {});
  const statsSelector = resultsDuck ? resultsDuck.selectors.getDuckState : deprecatedSelector;
  const {
    stats: statFilterValue = {}
  } = statsSelector(state);
  const filtersValues = filterDuck ? filterDuck.selectors.getRequestValues(state) : {};
  const eventValue = _get(event, 'target.value', '').toLowerCase();
  const {
    scrubbedEventValue,
    search,
    eventResult
  } = filterClickEventDataTransformer(eventValue, filterName, filtersValues, statFilterValue);
  const updatedFilterTitle = brand === 'kbb' ? 'Deliverable Cars Outside Radius' : filterTitle;
  const isMarketExtension = filterName === 'marketExtension';
  const updateCDLMarketExtension = () => {
    if (isMarketExtension) {
      return {
        cdl: {
          eventProperties: {
            action: 'Click',
            element: updatedFilterTitle,
            tagIndex: 'srp-0168'
          }
        }
      };
    }
    return {};
  };
  Google.sendClickEvent({
    eventElement: pageNameTransformer(pageName) || 'search-car',
    eventAction: 'filter',
    eventResult: eventResult || `${filterNameTransformer(filterName.split('|')[0])}::${sanitizeValue(scrubbedEventValue)}`,
    eventData: {
      search
    }
  }, option);
  Pixall.sendClickEvent({
    eventSource: isMarketExtension ? 'checkbox' : 'field',
    eventType: isMarketExtension ? 'click' : 'change',
    label: filterClickLabelTransformer(filterName, eventValue, updatedFilterTitle),
    pageId: isMarketExtension && brand === 'kbb' ? 'classlistnew' : 'fyc_srl',
    pageType: 'vlp',
    hasListingType: false,
    ...updateCDLMarketExtension()
  }, option);
  return {};
}
export async function sitemapFilterClick(_ref7, option) {
  let {
    data = {}
  } = _ref7;
  const eventData = _get(data, 'eventData', {});
  const sitemapContent = _get(data, 'sitemapContent', '').toLowerCase();
  Google.sendClickEvent({
    eventElement: 'search-dealer',
    eventAction: 'search',
    eventResult: `text-link::srp_dealer::${sitemapContent}`,
    eventData
  }, option);
  return {};
}

/**
 * @summary Handle click for Shop By Style on Advanced Keyword Search
 */
export async function shopByStyleClick(_ref8, option) {
  let {
    data = {},
    dataLayer = {}
  } = _ref8;
  const {
    tile = ''
  } = data;
  Pixall.sendClickEvent({
    pageType: 'vlp',
    eventSource: 'link',
    label: `tile::${tile}`
  }, dataLayer, option);
  return {};
}
export default {
  filterClick,
  numRecordsDropDownClick,
  paginationClick,
  searchRadiusClick,
  sortDropDownClick,
  sitemapFilterClick,
  shopByStyleClick
};